import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { FieldMetaState, FieldRenderProps } from 'react-final-form';
import { debounce } from '@mui/material';
import SuggestionItem from './SuggestionItem';
import { DadataLegalType } from '../../../enum/DadataLegalType';
import { DadataInnResponse } from '../../../structures/response/dadata/DadataInnResponse';
import { partySuggest } from '../../../api/dadata';

interface Props extends Partial<FieldRenderProps<string>> {
  onChange: (v: string) => void;
  onItemSelected: (data: DadataInnResponse) => void;
  onBlur: () => void;
  meta: FieldMetaState<string>;
  value: string;
  legalType: DadataLegalType;
  label?: string;
  getValue?: (v: DadataInnResponse) => string;
}

const LegalSuggest = ({
  onChange,
  value,
  meta,
  onBlur,
  legalType,
  onItemSelected,
  label,
  getValue,
}: Props) => {
  const [suggestions, setSuggestions] = useState<
    {
      value: string;
      data: DadataInnResponse;
    }[]
  >([]);
  const [inputValue, setInputValue] = useState('');
  let labelDefault: string;
  if (legalType === DadataLegalType.INDIVIDUAL) {
    labelDefault = 'Поиск по ИНН, ФИО или адресу';
  } else {
    labelDefault = 'Поиск по названию, ИНН или адресу';
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const fetchSuggestions = async (v: string) => {
    try {
      const response = await partySuggest(v, legalType);
      if (Array.isArray(response?.suggestions)) {
        setSuggestions(response.suggestions);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const fetchSuggestionsThrottled = useMemo(
    () => debounce(fetchSuggestions, 200),
    []
  );

  const handleItemSelect = (
    event: React.SyntheticEvent,
    data: { value: string; data: DadataInnResponse } | null
  ) => {
    if (data) {
      onItemSelected(data?.data);
      if (getValue) {
        onChange(getValue(data.data));
      }
    }
  };

  useEffect(() => {
    fetchSuggestionsThrottled(inputValue);
  }, [inputValue]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <Autocomplete
      options={suggestions}
      getOptionLabel={(option) => option.value}
      autoComplete
      autoHighlight
      autoSelect
      noOptionsText="По заданным параметрам ничего не найдено"
      filterOptions={(x) => x}
      onChange={handleItemSelect}
      onBlur={onBlur}
      inputValue={inputValue}
      renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option) => (
        <SuggestionItem key={option?.data?.inn} option={option} props={props} />
      )}
      renderInput={(params) => (
        <TextField
          error={meta.modified && meta.invalid}
          helperText={meta.error}
          {...params}
          onChange={handleChange}
          label={
            <span
              style={{
                whiteSpace: 'nowrap',
                display: 'inline-block',
                overflow: 'hidden',
                fontSize: '13px',
              }}
            >
              {label ?? labelDefault}
            </span>
          }
          margin="dense"
        />
      )}
    />
  );
};

LegalSuggest.defaultProps = {
  label: 'Поиск по названию, ИНН или адресу',
  getValue: (v: DadataInnResponse) => v?.name?.full_with_opf,
};

export default LegalSuggest;
